/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';
import {
  useDropzone, DropzoneOptions,
} from 'react-dropzone';
import {
  Controller, useFormContext, FieldValues, Path, FieldError,
} from 'react-hook-form';
import clsx from 'clsx';
import { BaseError } from '../Errors';
import { getFileSize } from '../../../utils/files';

interface DropzoneFieldProps<T extends FieldValues> extends Omit<DropzoneOptions, 'onDrop'> {
  name: Path<T>;
  label?: string;
  className?: string;
  dragActiveClassName?: string;
  children?: React.ReactNode;
  renderContent?: (isDragActive: boolean, files: File[]) => React.ReactNode;
  showPreview?: boolean;
  previewComponent?: (files: File[]) => React.ReactNode;
  multiple?: boolean;
  onDrop?: (files: File[]) => void;
}

function DropzoneFieldContent({
  isDragActive,
  multiple,
  disabled,
}: {
  isDragActive: boolean;
  multiple: boolean;
  disabled: boolean | undefined;
}) {
  if (disabled) {
    return (
      <>
        <img
          src="/static/img/icons/circle-file-upload.svg"
          width={146}
          alt=""
          style={{ opacity: 0.5 }}
        />
        <div className="dropzone-text text-muted">
          <span>
            Ingresa un RUT válido para habilitar la carga de documentos
          </span>
        </div>
      </>
    );
  }

  return (
    <>
      <img src="/static/img/icons/circle-file-upload.svg" width={146} alt="" />
      <div className="dropzone-text">
        {isDragActive ? (
          <span>
            Suelta
            {' '}
            {multiple ? 'los archivos' : 'el archivo'}
            {' '}
            aquí...
          </span>
        ) : (
          <>
            <span className="fw-bold text-decoration-underline">
              Sube aquí
            </span>
            {' '}
            o arrastra
            {' '}
            {multiple ? 'tus archivos' : 'tu archivo'}
            {' '}
            para
            {' '}
            {multiple ? 'adjuntarlos' : 'adjuntarlo'}
          </>
        )}
      </div>
    </>
  );
}

function DropzonePreview({ files }: { files: File[] }) {
  return (
    <ul className="p-0">
      {files.map((file: File) => {
        const fileSizeMB = getFileSize({ file, unit: 'MB' });
        return (
          <li className="list-unstyled" key={`${file.name}-${file.size}`} title={file.name}>
            <span className="d-inline-block text-truncate w-100">
              <span className="text-muted fs-xs">
                (
                {fileSizeMB}
                {' '}
                MB)
              </span>
              {' '}
              {file.name}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

export function DropzoneField<T extends FieldValues>({
  name,
  label,
  className,
  dragActiveClassName = 'dropzone--active',
  children,
  renderContent,
  showPreview = false,
  previewComponent,
  multiple = false,
  onDrop,
  ...dropzoneOptions
}: DropzoneFieldProps<T>) {
  const { control, getFieldState } = useFormContext<T>();
  const { error } = getFieldState(name);
  const fieldId = `dropzone-${name}`;

  return (
    <div className="w-100">
      {label && (
        <label htmlFor={fieldId} className="fs-sm fw-bold text-primary w-100">
          <span className="mb-2 d-inline-block">{label}</span>
        </label>
      )}

      <Controller
        name={name}
        control={control}
        render={({ field: { onChange, value } }) => (
          <DropzoneWrapper
            onChange={onChange}
            value={value}
            multiple={multiple}
            className={className}
            dragActiveClassName={dragActiveClassName}
            error={error}
            fieldId={fieldId}
            showPreview={showPreview}
            previewComponent={previewComponent}
            renderContent={renderContent}
            dropzoneOptions={dropzoneOptions}
            onDrop={onDrop}
            disabled={dropzoneOptions.disabled}
          >
            {children}
          </DropzoneWrapper>
        )}
      />
    </div>
  );
}

interface DropzoneWrapperProps {
  onChange: (files: File | File[] | null) => void;
  value: File | File[] | null;
  multiple: boolean;
  className?: string;
  dragActiveClassName: string;
  error: FieldError | undefined;
  fieldId: string;
  showPreview: boolean;
  previewComponent?: (files: File[]) => React.ReactNode;
  renderContent?: (isDragActive: boolean, files: File[]) => React.ReactNode;
  dropzoneOptions: Omit<DropzoneOptions, 'onDrop' | 'multiple'>;
  children?: React.ReactNode;
  onDrop?: (files: File[]) => void;
  disabled: boolean | undefined;
}

function DropzoneWrapper({
  onChange,
  value,
  multiple,
  className,
  dragActiveClassName,
  error,
  fieldId,
  showPreview,
  previewComponent,
  renderContent,
  dropzoneOptions,
  children,
  onDrop,
  disabled,
}: DropzoneWrapperProps) {
  const handleDrop = React.useCallback((acceptedFiles: File[]) => {
    if (onDrop) {
      onDrop(acceptedFiles);
    } else {
      const newFiles = multiple ? acceptedFiles : acceptedFiles[0] || null;
      onChange(newFiles);
    }
  }, [onChange, multiple, onDrop]);

  const {
    getRootProps, getInputProps, isDragActive,
  } = useDropzone({
    ...dropzoneOptions,
    multiple,
    onDrop: handleDrop,
  });

  const currentFiles = React.useMemo(() => {
    if (!value) return [];
    return Array.isArray(value) ? value : [value];
  }, [value]);

  let dropzoneContent = null;

  if (renderContent) {
    dropzoneContent = renderContent(isDragActive, currentFiles);
  } else if (children) {
    dropzoneContent = children;
  } else {
    dropzoneContent = (
      <DropzoneFieldContent isDragActive={isDragActive} multiple={multiple} disabled={disabled} />
    );
  }

  return (
    <div>
      <section
        {...getRootProps()}
        className={clsx(
          'dropzone',
          className,
          isDragActive && dragActiveClassName,
          error && 'is-invalid'
        )}
      >
        <input {...getInputProps()} className="d-none" id={fieldId} />
        {dropzoneContent}
      </section>

      {showPreview && currentFiles.length > 0 && (
        <div className="mt-3">
          {previewComponent ? (
            previewComponent(currentFiles)
          ) : (
            <DropzonePreview files={currentFiles} />
          )}
        </div>
      )}

      <BaseError message={error?.message} />
    </div>
  );
}
